import { useCallback, useSyncExternalStore } from 'react';
import { isPositiveNumber } from '@/domain/numbers';
import type { Market } from '@/domain/side';

/**
 * A symbol's last known price. `receivedAt` is when this client obtained it (ms since the epoch, client clock).
 * `stale` means it may no longer be current: it was obtained before the current connection was made (prices may have
 * moved during the gap), or it came from the snapshot of a server whose own stream had stalled, and no tick or fresh
 * snapshot has confirmed it since. A stale price is shown muted with a badge, never in full colour.
 */
export type PriceEntry = { price: number; receivedAt: number; stale: boolean };

/** One entry of a ReceivePrice or ReceiveFuturesPrice batch, or of the price snapshot. */
export type Tick = { symbol: string; lastPrice: number };

export type SnapshotOptions = {
  /**
   * False when the server's own stream had stalled (its newest price is old): the items fill in or replace prices that
   * are missing or stale, stay stale themselves, and do not count as live data.
   */
  fresh?: boolean;
};

export interface PriceStore {
  /**
   * Live prices. Only the symbols whose entry changed (a new price, or a stale price confirmed) are notified, so a
   * repeated batch notifies nobody. Ticks without a usable price (missing, zero, negative or not finite) are ignored.
   */
  applyTicks(market: Market, ticks: readonly Tick[], receivedAt?: number): void;
  /**
   * Seeds prices from a snapshot requested at `requestedAt` (taken before the request was sent). A symbol that ticked
   * at or after `requestedAt` keeps its tick, and so does a symbol seeded by a later snapshot.
   */
  applySnapshot(market: Market, items: readonly Tick[], requestedAt: number, options?: SnapshotOptions): void;
  /**
   * A (re)connect at `at`: every price of both markets obtained before then is marked stale until a tick or a fresh
   * snapshot confirms it, and so is anything applied later with an earlier time.
   */
  markStaleBefore(at: number): void;
  get(market: Market, symbol: string): PriceEntry | undefined;
  /** Called when this symbol's entry changes or is cleared. */
  subscribe(market: Market, symbol: string, listener: () => void): () => void;
  /** When the last live tick arrived, in any market; null before the first one. Proof that the socket is alive. */
  lastTickAt(): number | null;
  /**
   * When live data last arrived, in any market: a tick, or a fresh snapshot (counted at its request time). Null before
   * the first. The feed is only live while this is recent and newer than the current connection.
   */
  lastDataAt(): number | null;
  /**
   * Called at most once per applyTicks, applySnapshot, markStaleBefore or clear call that received or changed
   * something, however many symbols it touched (a batch arrives every second): listeners should only compare cheap
   * derived values.
   */
  subscribeAny(listener: () => void): () => void;
  /** Forgets every price (sign-out). */
  clear(): void;
}

type Listener = () => void;

type Slot = {
  entry: PriceEntry | undefined;
  /** The newest tick time or fresh snapshot request time this price is known to be current at. */
  freshAt: number;
  listeners: Set<Listener>;
};

function isUsable(tick: Partial<Tick> | null | undefined): tick is Tick {
  return !!tick && typeof tick.symbol === 'string' && tick.symbol.length > 0 && isPositiveNumber(tick.lastPrice);
}

function call(listeners: Iterable<Listener>): void {
  // A copy: a listener may unsubscribe while being called.
  for (const listener of [...listeners]) listener();
}

export function createPriceStore(): PriceStore {
  const markets: Record<Market, Map<string, Slot>> = { spot: new Map(), usdm: new Map() };
  /** Prices known only from before this time are stale (the last (re)connect). */
  const staleBefore: Record<Market, number> = { spot: -Infinity, usdm: -Infinity };
  const anyListeners = new Set<Listener>();
  let lastTick: number | null = null;
  let lastData: number | null = null;

  function slotOf(market: Market, symbol: string): Slot {
    const slots = markets[market];
    let slot = slots.get(symbol);
    if (!slot) {
      slot = { entry: undefined, freshAt: -Infinity, listeners: new Set() };
      slots.set(symbol, slot);
    }
    return slot;
  }

  /** Sets the slot's entry unless it would show exactly the same thing; true when it changed. */
  function put(slot: Slot, price: number, at: number, stale: boolean): boolean {
    if (slot.entry && slot.entry.price === price && slot.entry.stale === stale) return false;
    slot.entry = { price, receivedAt: at, stale };
    return true;
  }

  function notify(changed: Iterable<Slot>): void {
    for (const slot of changed) call(slot.listeners);
    call(anyListeners);
  }

  return {
    applyTicks(market, ticks, receivedAt = Date.now()) {
      const changed = new Set<Slot>();
      let received = false;

      for (const tick of ticks) {
        if (!isUsable(tick)) continue;
        received = true;

        const slot = slotOf(market, tick.symbol);
        slot.freshAt = Math.max(slot.freshAt, receivedAt);
        if (put(slot, tick.lastPrice, receivedAt, slot.freshAt < staleBefore[market])) changed.add(slot);
      }

      if (!received) return;
      lastTick = Math.max(lastTick ?? receivedAt, receivedAt);
      lastData = Math.max(lastData ?? receivedAt, receivedAt);
      notify(changed);
    },

    applySnapshot(market, items, requestedAt, { fresh = true } = {}) {
      const changed = new Set<Slot>();
      let received = false;
      const stale = !fresh || requestedAt < staleBefore[market];

      for (const item of items) {
        if (!isUsable(item)) continue;
        received = true;

        const slot = slotOf(market, item.symbol);
        if (slot.freshAt >= requestedAt) continue;

        if (fresh) {
          slot.freshAt = requestedAt;
        } else if (slot.entry && !slot.entry.stale) {
          // A stalled server's price never replaces one this client knows to be current.
          continue;
        }

        if (put(slot, item.lastPrice, requestedAt, stale)) changed.add(slot);
      }

      // An empty snapshot (right after an API restart) says nothing about which symbols have a price.
      if (!received) return;
      if (fresh) lastData = Math.max(lastData ?? requestedAt, requestedAt);
      notify(changed);
    },

    markStaleBefore(at) {
      const changed: Slot[] = [];

      for (const market of Object.keys(markets) as Market[]) {
        staleBefore[market] = Math.max(staleBefore[market], at);
        for (const slot of markets[market].values()) {
          if (!slot.entry || slot.entry.stale || slot.freshAt >= at) continue;
          slot.entry = { ...slot.entry, stale: true };
          changed.push(slot);
        }
      }

      if (changed.length > 0) notify(changed);
    },

    get(market, symbol) {
      return markets[market].get(symbol)?.entry;
    },

    subscribe(market, symbol, listener) {
      const slot = slotOf(market, symbol);
      slot.listeners.add(listener);

      return () => {
        slot.listeners.delete(listener);
        const slots = markets[market];
        if (!slot.entry && slot.listeners.size === 0 && slots.get(symbol) === slot) slots.delete(symbol);
      };
    },

    lastTickAt() {
      return lastTick;
    },

    lastDataAt() {
      return lastData;
    },

    subscribeAny(listener) {
      anyListeners.add(listener);
      return () => {
        anyListeners.delete(listener);
      };
    },

    clear() {
      const cleared: Slot[] = [];

      for (const slots of Object.values(markets)) {
        for (const [symbol, slot] of slots) {
          if (slot.entry) cleared.push(slot);
          slot.entry = undefined;
          slot.freshAt = -Infinity;
          if (slot.listeners.size === 0) slots.delete(symbol);
        }
      }

      lastTick = null;
      lastData = null;
      staleBefore.spot = -Infinity;
      staleBefore.usdm = -Infinity;
      notify(cleared);
    },
  };
}

/** The app-wide price store, fed by the hub (src/realtime/hub.ts). */
export const priceStore: PriceStore = createPriceStore();

/** A symbol's live price. Re-renders only when that symbol's entry changes (its price, or whether it is stale). */
export function usePrice(market: Market, symbol: string): PriceEntry | undefined {
  const subscribe = useCallback((listener: () => void) => priceStore.subscribe(market, symbol, listener), [market, symbol]);
  const getSnapshot = () => priceStore.get(market, symbol);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
